import * as _ from 'lodash'
import {AttributeFilterType, IdsAttribute, IdsFilterSpec, IdsMetric, IdsNode, IdsRow, IMetricDataSetProvider } from './PA.MetricDataSet.IF'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { IMetricDataProviderOptions } from './IMetricDataProviderOptions'
import {
    ICellMetric,
    ICubePerspectiveOptions,
    ICubeSource,
    IDataLocation
} from '../PA.HyperRectangle.Core/PA.HyperRectangle.IF'
import { IMessageProvider } from '../PA.Core/PA.Core.IF.Basic'
import { ErrorMessage_restate, ExceptionMessage_restate } from '../PA.Core/Basic/MessageUtils'
import { DimensionType, IHierarchy } from '../PA.Core/PA.Core.IF.Hierarchy'

// These types should not be exposed externally. Internal use only
interface IcsAttribute extends IdsAttribute {
    dimensionType: DimensionType
    Hierarchy: IHierarchy
    levelId: string
    levelDepth: number

    // These translate between CubeSource Ids and Reporting Framework Attribute/ Node Ids
    dctDsAttrIdByHierNodeId: { [nodeId: string]: number }
    dctHierNodeIdByDsNodeId: { [dsNodeId: number]: string }
}

// These types should not be exposed externally. Internal use only
interface IcsMetric extends IdsMetric {
    csMetricId: string
    cellMetric: ICellMetric
}

export class CubeSourceDataProvider implements IMetricDataSetProvider {
    private _createMessages: MessageProvider
    private _createOptions: IMetricDataProviderOptions
    private _name: string

    private _bLoaded: boolean
    private _onLoadComplete: Promise<void>

    private _dctAttribute: { [attributeId: string]: IcsAttribute }
    private _dctMetric: { [metricId: string]: IcsMetric }
    private _dctAttributeIdByDimTypeLevel: { [dimType: string]: { [dimType: string]: string } }
    private _dctLeafLevelByDimType: { [dimType: string]: string }

    private dctAttrNodes: { [attributeId: string]: { [wbId: number]: IdsNode } }
    private cubeSource: ICubeSource

    constructor(createOptions?: IMetricDataProviderOptions) {
        this._bLoaded = false
        this._createOptions = createOptions
        this._createMessages = new MessageProvider()
        this._name = createOptions?.name || `CubeSource Metric Provider ${createOptions?.objectName}`

        this._dctAttribute = {}
        this._dctMetric = {}
        this._dctAttributeIdByDimTypeLevel = {}
        this._dctLeafLevelByDimType = {}
        this.cubeSource = createOptions?.cubeSource
        this.dctAttrNodes = {}
    }

    get CreateOptions(): IMetricDataProviderOptions {
        return this._createOptions
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }

    get CreateMessages(): IMessageProvider {
        return this._createMessages
    }

    OnLoadComplete(): Promise<void> {
        const self = this
        if (this._onLoadComplete) return this._onLoadComplete

        const rv = (this._onLoadComplete = (async () => {
            const mp = this._createMessages
            //--
            try {
                if (_.isEmpty(self.cubeSource)) {
                    ErrorMessage_restate(mp, true, 'NoCubeSource', 'No Cube Source')
                    return
                }
                if (!self.cubeSource.bICubeSourceLoaded) await self.cubeSource.OnICubeSourceLoadComplete

                if (!self.cubeSource.bICubeSourceLoaded) {
                    ErrorMessage_restate(mp, true, 'CubeSourceNotLoaded', 'Unable to load CubeSource')
                    return
                }

                // 1. Create Attributes, one for each Dimension/ Hierarchy Level
                // 2. Create Metrics

                // 1. Create Attributes, one for each Dimension/ Hierarchy Level
                let dimTypes = self.cubeSource.getDimensions()
                for (let dimType of dimTypes) {
                    self._dctAttributeIdByDimTypeLevel[dimType] = {}

                    let hier = self.cubeSource.getDimensionHierarchy(dimType)

                    // For Each Level, Create Attribute
                    let arrLevels = hier.getLevels()
                    for (let level of arrLevels) {
                        //
                        let attributeId = `${dimType}-${level.LevelId}`
                        self._dctAttributeIdByDimTypeLevel[dimType][level.LevelId] = attributeId
                        self.dctAttrNodes[attributeId] = {}

                        let csAttrib: IcsAttribute = {
                            Id: attributeId,
                            Type: 'node',
                            Name: level.LevelName,
                            ShortName: level.LevelName,
                            SortOrder: 0,

                            // Cube Source Properties
                            dimensionType: dimType,
                            Hierarchy: hier,
                            levelId: level.LevelId,
                            levelDepth: level.LevelDepth,
                            dctDsAttrIdByHierNodeId: {},
                            dctHierNodeIdByDsNodeId: {}
                        }
                        let genWBId = 1
                        let levelNodes = hier.getNodesAtLevel(level.LevelId)
                        _.forEach(levelNodes, (hierNode) => {
                            let wbId = hierNode.bHasSourceKey ? +hierNode.SourceKey : genWBId++
                            csAttrib.dctDsAttrIdByHierNodeId[hierNode.NodeId] = wbId
                            csAttrib.dctHierNodeIdByDsNodeId[wbId] = hierNode.NodeId
                            self.dctAttrNodes[attributeId][wbId] = {
                                id: wbId,
                                id_ui: hierNode.NodeIdUI,
                                name: hierNode.NodeName,
                                nodeOrder: hierNode.NodeOrder
                            }
                        })

                        self._dctAttribute[attributeId] = csAttrib
                    }
                }

                // 2. Create Metrics
                let cubeMetrics = self.cubeSource.getMetrics()
                for (let cubeMtrc of cubeMetrics) {
                    let formatStr = '#,##0'
                    let decimalPoints = cubeMtrc?.RenderOptions?.decimalPlaces || 0
                    let bPercent = cubeMtrc.RenderOptions?.numberType == 'Percent'
                    if (
                        _.isBoolean(cubeMtrc?.RenderOptions?.showThousandsSeperator) &&
                        !cubeMtrc.RenderOptions.showThousandsSeperator
                    ) {
                        formatStr = '#0'
                    }
                    if (decimalPoints > 0) formatStr += '.'.padEnd(decimalPoints + 1, '0')
                    if (bPercent) formatStr += '%'

                    let csMetric: IcsMetric = {
                        Id: cubeMtrc.MetricId,
                        Name: cubeMtrc.MetricName,
                        ShortName: cubeMtrc.MetricName,
                        format: formatStr,
                        SortOrder: cubeMtrc.Sort,
                        csMetricId: cubeMtrc.MetricId,
                        cellMetric: cubeMtrc
                    }

                    self._dctMetric[csMetric.Id] = csMetric
                }

                this._bLoaded = true
            } catch (ex) {
                ExceptionMessage_restate(mp, ex)
            }
        })())

        return rv
    }

    get Name(): string {
        return this._name
    }

    getAttributes(): IdsAttribute[] {
        return _.values(this._dctAttribute)
    }

    getMetrics(): IdsMetric[] {
        return _.values(this._dctMetric)
    }

    getRowAttribute(row: IdsRow, attributeId: string): any {
        //const colName = this.dctColumnName[attributeId]
        return (<any>row || {})[attributeId]
    }

    getRowMetric(row: IdsRow, metricId: string): number {
        return (<any>row || {})[metricId]
    }

    getRowData(row: IdsRow, keyId: string): any {
        return (<any>(row || {}))?.keyStore?.[keyId]
    }

    setRowData(row: IdsRow, keyId: string, value: any): void {
        if (_.isEmpty(row)) return undefined
        const anyRow = <any>row
        anyRow.keyStore = anyRow.keyStore || {}
        anyRow.keyStore[keyId] = value
    }

    getRowAggregateMetric(rows: IdsRow[], metricId: string): number {
        const self = this
        //const mtr = self._dctMetricOutputCalc[metricId]
        //if (_.isEmpty(mtr?.aggregateExpression)) return undefined

        //const aggExpr = mtr.aggregateExpression

        //function getSymbol(row: IdsRow, symbolId: string): number {
        //    return self.getRowMetric(row, symbolId)
        //}

        //const aggMtrValue = ExpressionUtils.getAggregate(aggExpr, rows, getSymbol)
        //return aggMtrValue
        return 0
    }

    async getAttributeNodes(attributeId: string, ids: number[]): Promise<IdsNode[]> {
        const attr = this._dctAttribute[attributeId]
        if (attr?.Type != 'node') return []

        if (!(attributeId in this.dctAttrNodes)) this.dctAttrNodes[attributeId] = {}

        let dct = this.dctAttrNodes[attributeId]
        let dctRV: typeof dct = {}
        _.forEach(ids, (wbId) => {
            if (wbId in dct) dctRV[wbId] = dct[wbId]
        })
        return _.values(dctRV)
    }

    // CubeSource Perspective Option functions to output Reporting framework context.
    // 1. The filter context from the DimensionsFilters property
    // 2. The aggregation level (attributes) from the Dimensions property
    // 3. The metrics from MetricIds
    getPerspectiveOptionFilters(po: ICubePerspectiveOptions, filterType?: AttributeFilterType): IdsFilterSpec[] {
        const self = this

        let rv: IdsFilterSpec[] = []

        if(_.isEmpty(self.cubeSource))
            return []

        if(_.isEmpty(po))
            return []

        let df = po?.DimensionFilters || {}
        _.forEach(df, (f, dimType) => {
            if(_.isEmpty(f?.FilterByNodes))
                return

            let groupedByLevel = _.groupBy(f.FilterByNodes, n => n.NodeLevel)

            _.forEach(groupedByLevel, (arrNodes, levelId) => {
                let attrId = self._dctAttributeIdByDimTypeLevel[dimType]?.[levelId]
                let attr = self._dctAttribute[attrId]
                if(_.isEmpty(attr)) {
                    console.log("Unrecognized Node/ Level/ Attribute")
                    return
                }

                let wbIds = _.map(arrNodes, n => attr.dctDsAttrIdByHierNodeId[n.NodeId])
                rv.push({
                    id: attrId,
                    type: filterType || "server-side",
                    condition: {[attrId]: {inq: wbIds}}
                })
            })
        })

        return rv
    }

    getPerspectiveOptionsAttributes(po: ICubePerspectiveOptions): string[] {
        const self = this

        let rv: string[] = []

        if (_.isEmpty(self.cubeSource))
            return []

        if (_.isEmpty(po))
            return []

        let dims = po?.Dimensions || {}

        _.forEach(dims, (d, dimType) => {
            if(_.isEmpty(d?.LevelId))
                return

            let attrId = self._dctAttributeIdByDimTypeLevel[dimType]?.[d.LevelId]
            let attr = self._dctAttribute[attrId]
            if(_.isEmpty(attr)) {
                console.log("Unrecognized Attribute")
                return
            }
            rv.push(attr.Id)
        })

        return rv
    }

    getPerspectiveOptionsMetrics(po: ICubePerspectiveOptions): string[] {
        const self = this

        let rv: string[] = []

        if (_.isEmpty(self.cubeSource))
            return []

        if (_.isEmpty(po?.MetricIds))
            return []

        _.forEach(po?.MetricIds, (metricId) => {
            if(self.cubeSource.getMetric(metricId)) {
                rv.push(metricId)
            }
        })

        return rv
    }


    async getDataSetRowCount(arrAttributeIds: string[], filters: IdsFilterSpec[]): Promise<number> {
        const self = this
        //
        // get the number of leafs from the cubeSource
        // or create a perspective, execute and count the rows
        // or return constant
        return 99999
    }

    async getDataRows(
        arrAttributeIds: string[],
        arrMetricIds: string[],
        filters: IdsFilterSpec[],
        rowCountThreshold?: number,
        bCountOnly?: boolean
    ): Promise<IdsRow[]> {
        const self = this

        // TODO/ Test
        // 1. Create Perspective Options from parameters
        //  a. For Each Dimension, only include the lowest level attribute
        //  b. Iterate Over Filters to create Dimension Filters
        //     supports only 2 types of filters,
        //          {attributeId: number}
        //          or
        //          {attributeId: {inq: number[]}
        //      all other filters will be ignored
        //
        //  c. Add Metrics
        //
        // 2. Create Perspective
        // 3. Map CubeSource Rows to JSON rows
        // 4. For Attributes excluded in step 1.a., derive them using the hierarchy, update JSON Row
        // X. Optional, add support for output calc metrics
        // 5. return JSON rows

        // Determine Aggregation Level/ Filters
        let po: ICubePerspectiveOptions = {
            bEnabled: false,
            DimensionFilters: {},
            Dimensions: {},
            MetricIds: []
        }
        // 1. Create Perspective Options from parameters

        // 1.a. For Each Dimension, only include the lowest level attribute
        let arrAttributesInPO: string[] = []
        let arrAttributesDerived: string[] = []
        let dctLowest: { [dimType: string]: IcsAttribute } = {}

        // Determine the Lowest Level (highest depth) by Dimension
        _.forEach(_.uniq(arrAttributeIds), (attributeId) => {
            let csAttr = self._dctAttribute[attributeId]
            if (!csAttr) return
            if (csAttr.dimensionType in dctLowest) {
                // compare Depth
                let curLowest = dctLowest[csAttr.dimensionType]
                if (curLowest.levelDepth < csAttr.levelDepth) {
                    arrAttributesDerived.push(curLowest.Id)
                    dctLowest[csAttr.dimensionType] = csAttr
                }
            } else {
                dctLowest[csAttr.dimensionType] = csAttr
            }
        })
        arrAttributesInPO = _.filter(
            _.clone(arrAttributeIds),
            (attrId) => attrId in self._dctAttribute && !_.includes(arrAttributesDerived, attrId)
        )

        // Update Perspective Grain from the Lowest Level Attributes
        _.forEach(dctLowest, (attr, key) => {
            po.Dimensions[attr.dimensionType] = {
                LevelId: attr.levelId
            }
        })

        // 1.b. Iterate Over Filters to create Dimension Filters
        // TODO: Test
        _.forEach(filters, (f) => {
            if (_.isEmpty(f.condition)) return
            let filterNodeIds: string[] = []
            _.forEach(_.keys(f.condition), (attributeId) => {
                if (!(attributeId in self._dctAttribute)) {
                    // Log msg
                    return
                }

                let attr = self._dctAttribute[attributeId]
                // @ts-expect-error "Making assumption on structure for now"
                let val = f.condition[attributeId]
                if (_.isNumber(val)) {
                    // Single value case
                    let wbId = val
                    //-- convert number to nodeId
                    let nodeId = attr.dctHierNodeIdByDsNodeId[wbId]
                    if (!_.isEmpty(nodeId)) filterNodeIds.push(nodeId)
                } else if (_.isPlainObject(val)) {
                    // check for inq oper
                    let operator = _.first(_.keys(val))
                    if (_.includes(['in', 'inq'], operator)) {
                        let arrWbIds = <number[]>val[operator]
                        _.forEach(arrWbIds, (wbId) => {
                            let nodeId = attr.dctHierNodeIdByDsNodeId[wbId]
                            if (!_.isEmpty(nodeId)) filterNodeIds.push(nodeId)
                        })
                    }
                }

                if (!_.isEmpty(filterNodeIds)) {
                    if (_.isEmpty(po.DimensionFilters[attr.dimensionType]))
                        po.DimensionFilters[attr.dimensionType] = { FilterByNodes: [] }

                    _.forEach(_.uniq(filterNodeIds), (nodeId) => {
                        let node = attr.Hierarchy.getNode(nodeId)
                        if (!_.isEmpty(node)) po.DimensionFilters[attr.dimensionType].FilterByNodes.push(node)
                    })
                }
            })
        })

        // 1.c. Metric Ids should match, add them
        _.forEach(arrMetricIds, (metricId) => {
            if (metricId in self._dctMetric) po.MetricIds.push(metricId)
        })

        // 2. Create Perspective
        let perspective = self.cubeSource.createPerspective('CubeSourceDataSet', po)
        await perspective.OnCubePerspectiveLoadComplete

        // 3. Map CubeSource Rows to JSON rows
        let rv: { [id: string]: any }[] = []

        _.forEach(perspective.Cells, (cell) => {
            let dsRow: { [id: string]: any } = {}

            _.forEach(arrAttributesInPO, (attributeId) => {
                let csAttr = self._dctAttribute[attributeId]
                let nodeId = cell.getKey(csAttr.dimensionType)
                dsRow[attributeId] = csAttr.dctDsAttrIdByHierNodeId[nodeId] || 0
            })

            // 4. For Attributes excluded in step 1.a., derive them using the hierarchy, update JSON Row
            _.forEach(arrAttributesDerived, (attributeId) => {
                let csAttr = self._dctAttribute[attributeId]
                let nodeId = cell.getKey(csAttr.dimensionType)

                let hier = perspective.getDimensionHierarchy(csAttr.dimensionType)
                let rowNode = hier.getNode(nodeId)
                let dctPrntNode = hier.getParents(nodeId, csAttr.levelId)
                let prnt = dctPrntNode[nodeId]
                let wbIdPrnt = csAttr.dctDsAttrIdByHierNodeId[prnt?.NodeId] || 0
                dsRow[attributeId] = wbIdPrnt
            })

            _.forEach(po.MetricIds, (metricId) => {
                dsRow[metricId] = cell.getCellMetricValue(metricId)
            })

            rv.push(dsRow)
        })

        // 5. return JSON rows
        return rv
    }

    async getDistinctValues(attributeId: string, filters?: IdsFilterSpec[]): Promise<any[]> {
        const self = this

        // TODO:
        // If at the Leaf Level,
        // You can use the CubeSource API only
        // If at the Aggregate Level, you will have to also use the hierarchy
        if (!(attributeId in self._dctAttribute)) return []
        let attr = self._dctAttribute[attributeId]
        let distinctLeafKeys = await self.cubeSource.getDistinctLeafKeys([attr.dimensionType], {
            dataLocation: IDataLocation.RemoteAvailable
        })
        let leafNodeIds = distinctLeafKeys[attr.dimensionType]
        // Now you have to check if your at the leaf level or an aggregate, to get the real unique set.

        return []

    }

    async getValueRange(attributeId: string, filters?: IdsFilterSpec[]): Promise<{ min: any; max: any }> {
        // Cube sources don't have attributes of type number, or string
        // So return undefined range
        return { min: undefined, max: undefined }
    }
}